import { useAtomValue, useSetAtom } from "jotai";
import { startTransition, useEffect, useState } from "react";

import { atoms } from "#/lib/atoms/atoms";
import { useMerchantLocations } from "#/hooks/queries/use-merchant-locations";

export default function useLocations() {
  const [selectedLocations, setSelectedLocations] = useState([]);
  const currentLocationId = useAtomValue(atoms.currentLocationId);
  const setCurrentLocationId = useSetAtom(atoms.currentLocationId);

  const { data, loading: isLoading } = useMerchantLocations();

  useEffect(() => {
    if (data?.length) {
      // Re-trigger selection whenever the current location is cleared or
      // invalidated (e.g. deleting the active location) so the POS gate's
      // isSettling never gets stuck true with no recovery on the same mount.
      const hasValidSelection =
        currentLocationId != null &&
        data.some((location) => location._id === currentLocationId);
      if (hasValidSelection) return;

      const found = data?.find(
        (i) => i?._id === JSON.parse(localStorage.getItem("currentLocationId")),
      );

      // Defer through startTransition so the resulting Suspense re-key
      // (currentLocationId feeds useCatalogDetailsQuery / useMenusQuery /
      // useMenuQuery / useCatalogQuery / useConnectedDspLocation /
      // useMenuSchedules) doesn't unmount the menu page and flash its
      // fallback while the new data is in flight.
      startTransition(() => {
        if (found) {
          setCurrentLocationId(found._id);
        } else {
          setCurrentLocationId(data[0]._id);
        }
      });
    } else if (data && data.length === 0) {
      startTransition(() => {
        setCurrentLocationId(null);
      });
    }
  }, [data, setCurrentLocationId, currentLocationId]);

  const handleSelectedLocation = (selectedLocation) => {
    setSelectedLocations((prevState) => {
      if (prevState.includes(selectedLocation._id)) {
        return [...prevState.filter((item) => item !== selectedLocation._id)];
      }
      return [...prevState, selectedLocation._id];
    });
  };

  return {
    data: data || [],
    handleSelectedLocation,
    // this is needed because without it we prompt for pos onboarding before actual load
    // https://github.com/hookedapp/stream-dashboard-web/blob/930f36e6e75375c61004eeb6a3506f50a879602e/lib/atoms/queries.ts#L40
    loading: isLoading,
    selectedLocations,
    setLocation: setCurrentLocationId,
  };
}
